import { List } from 'immutable';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Popover } from 'rsuite';
import { PROPERTY_DATA_FILTER_KEYS, PROPERTY_DATA_KV_MAP } from '@/constants';
import useRentalscapeConfig from '@/hooks/use-rentalscape-config';
import * as mapActions from '@/map/map-actions';
import { MapboxInstanceMgr } from '@/map/map-instance';
import * as mapSelectors from '@/map/map-selectors';
import * as sections from '@/map/map-viewer/map-sidebar-sections';
import { initFilterParams } from '@/property-filter/filter-constants';
import * as filterStyles from './filter-popover-styles';

function PopoverFooter(props) {
  const { handleClearFilters, onClose } = props;
  return (
    <filterStyles.PopoverFooterWrapper>
      <filterStyles.ClearFilterButton onClick={handleClearFilters}>
        <filterStyles.FilterTextWrapper>
          Clear filters
        </filterStyles.FilterTextWrapper>
      </filterStyles.ClearFilterButton>
      <Button onClick={onClose} appearance="primary" style={{ width: '120px' }}>
        Done
      </Button>
    </filterStyles.PopoverFooterWrapper>
  );
}
const FilterPopoverComponent = React.forwardRef((props, ref) => {
  const {
    availableViolations,
    availablePlatforms,
    platformIcons,
    strComplaintsAvailable,
    className,
    setSingleFilterParam,
    placement,
    positionLeft,
    positionTop,
    filterButtonRef,
    onClose,
    style,
    setCurrentPage,
  } = props;

  const dispatch = useDispatch();
  const mapFilterParams = useSelector(mapSelectors.mapFilterParams);
  const setFilterParams = useCallback(
    (params) =>
      dispatch(mapActions.setFilterParams({ mapFilterParams: params })),
    [dispatch]
  );

  const rentalscapeConfig = useRentalscapeConfig();

  const handleFilterChange = useCallback(
    (groupName, type, value) => {
      const newFilterParams = mapFilterParams.setIn([groupName, type], value);
      setFilterParams(newFilterParams);
    },
    [setFilterParams, mapFilterParams]
  );

  const handleMultipleFilterChanges = useCallback(
    (changes) => {
      const newFilterParams = changes.reduce(
        (params, { groupName, type, value }) =>
          params.setIn([groupName, type], value),
        mapFilterParams
      );
      setFilterParams(newFilterParams);
    },
    [setFilterParams, mapFilterParams]
  );

  const handleSingleFilterChange = useCallback(
    (groupName, option, value) =>
      setSingleFilterParam({
        groupName,
        option,
        value,
      }),
    [setSingleFilterParam]
  );

  const handleHOACheckPickerChange = useCallback(
    (selectedHOAs) => {
      /**
       * Conversion to an Immutable List is required.
       * "hoa" is defined as an empty array within "initFilterParams".
       *    However, because the entire JS object is parsed by Immutable's "fromJS" method the simple JS array
       *      is converted to an Immutable List.
       */
      const tmpNewFilterParams = mapFilterParams.set('hoa', List(selectedHOAs));
      setFilterParams(tmpNewFilterParams);
      // The function that eventually uses "value" expects a JS array or a list-like struct with the "forEach" method
      MapboxInstanceMgr.getInstance().drawFilteredZoning(selectedHOAs);
    },
    [mapFilterParams, setFilterParams]
  );

  const handleClearFilters = useCallback(() => {
    MapboxInstanceMgr.getInstance().resetFilteredZoneColor();
    setFilterParams(initFilterParams);
  }, [mapFilterParams, setFilterParams]);

  return (
    <Popover ref={ref} className={className} onClose={onClose}>
      <filterStyles.PopoverContentWrapper>
        <sections.AddressIdentificationSection
          mapFilterParams={mapFilterParams}
          handleCheckboxOnChange={handleFilterChange}
        />
        <sections.NewSTRLicenseSection
          mapFilterParams={mapFilterParams}
          handleCheckboxOnChange={handleFilterChange}
        />
        <sections.RentalTypeSection
          mapFilterParams={mapFilterParams}
          handleCheckboxOnChange={handleFilterChange}
        />
        <sections.VerificationSection
          mapFilterParams={mapFilterParams}
          handleCheckboxOnChange={handleFilterChange}
        />
        {rentalscapeConfig?.showOwnerOccupied && (
          <sections.DiscreteValueFilterSection
            mapFilterParams={mapFilterParams}
            handleCheckboxOnChange={handleFilterChange}
            groupName={PROPERTY_DATA_FILTER_KEYS.hoe}
            valueOptions={[
              {
                label: 'Claiming HOE',
                value: PROPERTY_DATA_KV_MAP.hoe.claimingHoe,
              },
              {
                label: 'Not Claiming HOE',
                value: PROPERTY_DATA_KV_MAP.hoe.notClaimingHoe,
              },
            ]}
            sectionTitle="Homeowner Exemption"
          />
        )}
        <sections.ListingVisibilitySection
          mapFilterParams={mapFilterParams}
          handleCheckboxOnChange={handleFilterChange}
        />
        <sections.ViolationsSection
          mapFilterParams={mapFilterParams}
          handleSingleFilterChange={handleSingleFilterChange}
          availableViolations={availableViolations}
        />
        {strComplaintsAvailable && (
          <sections.ComplaintsSection
            mapFilterParams={mapFilterParams}
            handleSingleFilterChange={handleSingleFilterChange}
          />
        )}
        <sections.ActivitySection
          mapFilterParams={mapFilterParams}
          handleCheckboxOnChange={handleFilterChange}
        />
        <sections.OccupancySection
          mapFilterParams={mapFilterParams}
          handleSliderChange={handleFilterChange}
        />
        <sections.ResidentialTypeSection
          mapFilterParams={mapFilterParams}
          handleCheckboxOnChange={handleFilterChange}
        />
        <sections.RentalStructureSection
          mapFilterParams={mapFilterParams}
          handleCheckboxOnChange={handleFilterChange}
        />
        <sections.UnitTypeSection
          mapFilterParams={mapFilterParams}
          handleCheckboxOnChange={handleFilterChange}
        />
        <sections.OnlinePlatformsSection
          mapFilterParams={mapFilterParams}
          handleMultipleFilterChanges={handleMultipleFilterChanges}
          availablePlatforms={availablePlatforms}
          platformIcons={platformIcons}
        />
        <sections.HoaSection
          mapFilterParams={mapFilterParams}
          onChange={handleHOACheckPickerChange}
          onClean={() => handleHOACheckPickerChange([])}
        />
      </filterStyles.PopoverContentWrapper>
      <PopoverFooter
        handleClearFilters={handleClearFilters}
        onClose={onClose}
      />
    </Popover>
  );
});

export const FilterPopover = FilterPopoverComponent;
